import { css } from '@linaria/core';
import { SwapLeftOutlined, FilterOutlined } from '@ant-design/icons';
import type { HeaderCellProps } from '../HeaderCell';
import type { SortDirection } from '../types';
import { Space, Popover, Input, Checkbox, Button, Typography } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { formatMessage } from '../locales';

const headerSortCell = css`
  display: flex;
`;

const headerSortCellClassname = `rdg-header-sort-cell ${headerSortCell}`;

const headerSortName = css`
  flex-grow: 1;
  overflow: hidden;
  overflow: clip;
  text-overflow: ellipsis;
`;

const downClassname = css`
  transform: rotate(-90deg);
  font-size: 14px;
  margin-right: -8px;
`;

const upClassname = css`
  transform: rotate(90deg);
  font-size: 14px;
`;

const filterBtnClassName = css`
  cursor: pointer;
  color: var(--text-color-primary);
  &:hover {
    color: #1890ff;
  }
`;

const { Text } = Typography;

const headerSortNameClassname = `rdg-header-sort-name ${headerSortName}`;

type SharedHeaderCellProps<R, SR> = Pick<
  HeaderCellProps<R, SR>,
  'column' | 'sortInfo' | 'enableSortRow' | 'onSort' | 'cellFilters' | 'onCellFiltersChange'
>;

interface Props<R, SR> extends SharedHeaderCellProps<R, SR> {
  children: React.ReactNode;
  checkedFilters?: Set<string | number>;
}

function SortIcon({ direction }: { direction: SortDirection }) {
  return (
    <span>
      <SwapLeftOutlined
        className={downClassname}
        style={direction === 'DESC' ? { color: '#1890ff' } : undefined}
      />
      <SwapLeftOutlined
        className={upClassname}
        style={direction === 'ASC' ? { color: '#1890ff' } : undefined}
      />
    </span>
  );
}

function SortableHeaderCell<R, SR>({
  column,
  onSort,
  sortInfo: { columnKey: sortColumn, direction: sortDirection },
  enableSortRow,
  cellFilters,
  checkedFilters,
  onCellFiltersChange,
  children,
}: Props<R, SR>) {
  sortDirection = (sortColumn === column.key && sortDirection) || 'NONE';
  const [cellFilterVisible, setCellFilterVisible] = useState(false);
  const _onCellFiltersChange = useCallback(
    (columnKey: string, newFilters?: Set<string | number>) => {
      onCellFiltersChange(columnKey, newFilters);
      setCellFilterVisible(false);
    },
    [onCellFiltersChange],
  );

  function onClick(e: React.MouseEvent<HTMLSpanElement>) {
    e.stopPropagation();
    if (!onSort) {
      return;
    }
    const { sortDescendingFirst } = column;
    let direction: SortDirection;
    switch (sortDirection) {
      case 'ASC':
        direction = sortDescendingFirst ? 'NONE' : 'DESC';
        break;
      case 'DESC':
        direction = sortDescendingFirst ? 'ASC' : 'NONE';
        break;
      default:
        direction = sortDescendingFirst ? 'DESC' : 'ASC';
        break;
    }
    onSort(column.key, direction);
  }

  return (
    <span className={headerSortCellClassname}>
      <span className={headerSortNameClassname}>{children}</span>
      <Space style={{ color: 'rgba(0,0,0,0.45)' }} size={3}>
        {!!cellFilters && (
          <Popover
            destroyTooltipOnHide
            trigger="click"
            placement="bottom"
            open={cellFilterVisible}
            onOpenChange={(v) => {
              setCellFilterVisible(v);
            }}
            arrow={false}
            content={
              <FilterContent
                cellFilters={cellFilters}
                checkedFilters={checkedFilters}
                columnKey={column.key}
                onCellFiltersChange={_onCellFiltersChange}
              />
            }
          >
            <span
              onClick={(e) => {
                e.stopPropagation();
              }}
              className={filterBtnClassName}
            >
              <FilterOutlined style={checkedFilters?.size ? { color: '#1890ff' } : undefined} />
            </span>
          </Popover>
        )}
        {!!(enableSortRow && column.sortable) && (
          <span style={{ cursor: 'pointer', color: 'var(--text-color-primary)' }} onClick={onClick}>
            <SortIcon direction={sortDirection} />
          </span>
        )}
      </Space>
    </span>
  );
}

export default React.memo(SortableHeaderCell) as typeof SortableHeaderCell;
const MAX_FILTER_COUNT = 200;

function FilterContent({
  cellFilters,
  checkedFilters,
  onCellFiltersChange,
  columnKey,
}: {
  cellFilters: Set<string | number>;
  checkedFilters?: Set<string | number>;
  onCellFiltersChange: (columnKey: string, newFilters?: Set<string | number>) => void;
  columnKey: string;
}) {
  const [searchValue, setSearchValue] = useState<string>();
  const [checkedValue, setCheckedValue] = useState<(string | number)[]>(
    Array.from(checkedFilters ?? []),
  );

  const [options, isOutLimit] = useMemo(() => {
    let isOutLimit = false;
    let _options: (string | number)[] = [...cellFilters].filter((v: string | number) => {
      if (typeof v === 'number') {
        v = `${v}`;
      }
      return v?.toLowerCase?.().indexOf(searchValue?.toLowerCase() ?? '') > -1;
    });
    if (_options.length > MAX_FILTER_COUNT) {
      _options = _options.slice(0, MAX_FILTER_COUNT);
      isOutLimit = true;
    }
    return [_options, isOutLimit];
  }, [cellFilters, searchValue]);

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Space direction="vertical">
        <Input.Search
          autoFocus
          onClick={function (e) {
            e.currentTarget.focus();
          }}
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
          }}
        />
        {isOutLimit ? (
          <Text type="secondary">
            {formatMessage('headerCells.SortableHeaderCell.maxLimitText')}
          </Text>
        ) : null}
        <div
          style={{
            maxHeight: 300,
            overflow: 'auto',
            border: '1px solid #ddd',
            padding: 12,
            width: 300,
          }}
        >
          <Checkbox.Group
            value={checkedValue}
            onChange={(v) => {
              setCheckedValue(v);
            }}
          >
            <Space direction="vertical">
              {options.includes('null') && (
                <Checkbox key={'null'} value={'null'}>
                  null
                </Checkbox>
              )}
              {options
                .map((option) => {
                  if (option === 'null') {
                    return null;
                  }
                  return (
                    <Checkbox key={option} value={option}>
                      {option}
                    </Checkbox>
                  );
                })
                .filter(Boolean)}
            </Space>
          </Checkbox.Group>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Space>
            <a
              onClick={() => {
                setCheckedValue(options);
              }}
            >
              {formatMessage('headerCells.SortableHeaderCell.checkAll')}
            </a>
            <a
              onClick={() => {
                setCheckedValue([]);
              }}
            >
              {formatMessage('headerCells.SortableHeaderCell.cancelAll')}
            </a>
          </Space>
          <Space>
            <Button
              onClick={() => {
                onCellFiltersChange(columnKey, checkedFilters);
              }}
            >
              {formatMessage('headerCells.SortableHeaderCell.cancel')}
            </Button>
            <Button
              onClick={() => {
                onCellFiltersChange(columnKey, new Set(checkedValue));
              }}
              type="primary"
            >
              {formatMessage('headerCells.SortableHeaderCell.ok')}
            </Button>
          </Space>
        </div>
      </Space>
    </div>
  );
}
